import React, { useRef, useEffect, useState, useCallback } from 'react';
import { animate } from 'animejs';
import './SettingsKnob.css';

interface SettingsKnobProps {
  label: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
  displayLabels?: Record<string, string>;
}

const SettingsKnob: React.FC<SettingsKnobProps> = ({
  label,
  options,
  value,
  onChange,
  displayLabels = {},
}) => {
  const knobRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const currentAngleRef = useRef(0);

  // Calculate angle for each option (distributed evenly around the knob)
  const getAngleForIndex = useCallback((index: number): number => {
    const totalOptions = options.length;
    // Start from top (-90deg) and distribute clockwise
    const anglePerOption = 360 / totalOptions;
    return -90 + index * anglePerOption;
  }, [options.length]);

  // Get current option index
  const getCurrentIndex = useCallback((): number => {
    return options.indexOf(value);
  }, [options, value]);

  // Snap angle to nearest option
  const snapToNearestOption = useCallback((angle: number): number => {
    const totalOptions = options.length;
    const anglePerOption = 360 / totalOptions;
    
    // Normalize angle to 0-360 range
    let normalizedAngle = ((angle + 90) % 360 + 360) % 360;
    
    // Find nearest snap point
    const snapIndex = Math.round(normalizedAngle / anglePerOption) % totalOptions;
    return getAngleForIndex(snapIndex);
  }, [options.length, getAngleForIndex]);

  // Animate knob to specific angle
  const animateToAngle = useCallback((targetAngle: number) => {
    if (!indicatorRef.current) return;

    animate(indicatorRef.current, {
      rotate: targetAngle,
      duration: 300,
      ease: 'outElastic(1, 0.5)',
      onUpdate: () => {
        currentAngleRef.current = targetAngle;
      }
    });
  }, []);

  // Initialize knob position based on current value
  useEffect(() => {
    const currentIndex = getCurrentIndex();
    if (currentIndex !== -1) {
      const angle = getAngleForIndex(currentIndex);
      currentAngleRef.current = angle;
      if (indicatorRef.current) {
        indicatorRef.current.style.transform = `rotate(${angle}deg)`;
      }
    }
  }, [value, getCurrentIndex, getAngleForIndex]);

  // Handle option click
  const handleOptionClick = (option: string, index: number) => {
    const targetAngle = getAngleForIndex(index);
    animateToAngle(targetAngle);
    onChange(option);
  };

  // Handle mouse/touch events for dragging
  const handleMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (!isDragging || !knobRef.current) return;

    const knobRect = knobRef.current.getBoundingClientRect();
    const centerX = knobRect.left + knobRect.width / 2;
    const centerY = knobRect.top + knobRect.height / 2;

    let clientX: number, clientY: number;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    const angle = Math.atan2(clientY - centerY, clientX - centerX) * (180 / Math.PI);
    currentAngleRef.current = angle;
    
    if (indicatorRef.current) {
      indicatorRef.current.style.transform = `rotate(${angle}deg)`;
    }
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    // Snap to nearest option
    const snappedAngle = snapToNearestOption(currentAngleRef.current);
    animateToAngle(snappedAngle);

    // Find which option this corresponds to
    const totalOptions = options.length;
    const anglePerOption = 360 / totalOptions;
    let normalizedAngle = ((snappedAngle + 90) % 360 + 360) % 360;
    const optionIndex = Math.round(normalizedAngle / anglePerOption) % totalOptions;
    
    if (options[optionIndex] !== value) {
      onChange(options[optionIndex]);
    }
  }, [isDragging, snapToNearestOption, animateToAngle, options, value, onChange]);

  // Add global mouse/touch listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleMouseMove);
      window.addEventListener('touchend', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleMouseMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Get display label for an option
  const getDisplayLabel = (option: string): string => {
    if (displayLabels[option]) {
      return displayLabels[option];
    }
    // Convert snake_case to Title Case
    return option
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="settings-knob-container">
      <div className="settings-knob-label">{label}</div>
      <div className="settings-knob-wrapper">
        <div 
          className={`settings-knob ${isDragging ? 'dragging' : ''}`}
          ref={knobRef}
          onMouseDown={handleMouseDown}
          onTouchStart={handleMouseDown}
        >
          <div className="settings-knob-inner">
            <div className="settings-knob-indicator" ref={indicatorRef}>
              <div className="settings-knob-pointer" />
            </div>
            <div className="settings-knob-center" />
          </div>
        </div>
        
        {/* Option labels positioned around the knob */}
        <div className="settings-knob-options">
          {options.map((option, index) => {
            const angle = getAngleForIndex(index);
            // Position labels around the knob - use larger radius for left/right positions
            const baseRadius = 110;
            const radians = (angle - 90) * (Math.PI / 180);
            // Add extra horizontal spacing for labels on left/right sides
            const horizontalFactor = Math.abs(Math.cos(radians));
            const radius = baseRadius + (horizontalFactor * 20);
            const x = Math.cos(radians) * radius;
            const y = Math.sin(radians) * radius;
            
            return (
              <button
                key={option}
                className={`settings-knob-option ${value === option ? 'active' : ''}`}
                style={{
                  transform: `translate(${x}px, ${y}px)`,
                }}
                onClick={() => handleOptionClick(option, index)}
              >
                {getDisplayLabel(option)}
              </button>
            );
          })}
        </div>
      </div>
      <div className="settings-knob-value">{getDisplayLabel(value)}</div>
    </div>
  );
};

export default SettingsKnob;

